import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Menu, Globe, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';
import PDFExportButton from './PDFExportButton';

const sections = [
  { path: '/', key: 'home' },
  { path: '/marche', key: 'market' },
  { path: '/marche-garderies', key: 'marketDaycare' },
  { path: '/vision', key: 'vision' },
  { path: '/produit', key: 'product' },
  { path: '/propriete-intellectuelle', key: 'pi' },
  { path: '/paiements', key: 'payments' },
  { path: '/plan-execution', key: 'execution' },
  { path: '/gtm', key: 'gtm' },
  { path: '/roadmap', key: 'roadmap' },
  { path: '/resume-executif', key: 'resume' },
  { path: '/modele', key: 'model' },
  { path: '/organisation', key: 'org' },
  { path: '/valorisation', key: 'valuation' },
  { path: '/levee-de-fonds', key: 'fundraising' },
  { path: '/synthese-investisseur', key: 'investorSummary' },
  { path: '/previsions', key: 'forecast' }
];

const sectionNames = {
  fr: {
    home: 'Synthèse Globale',
    market: 'Marché — Écoles',
    marketDaycare: 'Marché — Garderies',
    vision: 'Vision & Objectifs',
    product: 'Produit & Expérience',
    pi: 'Propriété Intellectuelle',
    payments: 'Paiements & Sécurité',
    execution: 'Plan d\'Exécution',
    gtm: 'Go-to-Market',
    roadmap: 'Roadmap & Jalons',
    resume: 'Résumé Exécutif',
    model: 'Modèle d\'Affaires',
    org: 'Organisation & Coûts',
    valuation: 'Valorisation',
    fundraising: 'Levée de Fonds',
    investorSummary: 'Synthèse Investisseur',
    forecast: 'Prévisions Financières'
  },
  en: {
    home: 'Global Overview',
    market: 'Market — Schools',
    marketDaycare: 'Market — Daycares',
    vision: 'Vision & Objectives',
    product: 'Product & Experience',
    pi: 'Intellectual Property',
    payments: 'Payments & Security',
    execution: 'Execution Plan',
    gtm: 'Go-to-Market',
    roadmap: 'Roadmap & Milestones',
    resume: 'Executive Summary',
    model: 'Business Model',
    org: 'Organization & Costs',
    valuation: 'Valuation',
    fundraising: 'Fundraising',
    investorSummary: 'Investor Summary',
    forecast: 'Financial Forecast'
  }
};

export default function StickyNavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const t = sectionNames[language];
  const [menuOpen, setMenuOpen] = React.useState(false);

  const currentIndex = sections.findIndex(s => s.path === location.pathname);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < sections.length - 1;

  const handlePrev = () => {
    if (hasPrev) {
      navigate(sections[currentIndex - 1].path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => document.activeElement?.blur(), 50);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      navigate(sections[currentIndex + 1].path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => document.activeElement?.blur(), 50);
    }
  };

  const toggleMenu = () => {
    window.dispatchEvent(new Event('toggleSidebar'));
    setMenuOpen(!menuOpen);
  };

  return (
    <div className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-lime-500/20 transition-all duration-300 hover:bg-black/90">
      <div className="max-w-[1600px] mx-auto px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {/* Menu Button */}
          <button
            onClick={toggleMenu}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-lime-500/10 rounded-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(168,213,48,0.6)]">
            <Menu className="h-5 w-5 text-lime-400" />
            <span className="text-sm text-lime-400 font-bold hidden sm:inline">Menu</span>
          </button>

          {/* Language Toggle Button */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-lime-500/20 hover:bg-lime-500/30 text-lime-400 border border-lime-500/50 rounded-lg transition-all duration-200 shadow-sm"
          >
            <Globe className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold">{language === 'fr' ? 'EN' : 'FR'}</span>
          </button>

          {/* Dot separator */}
          <div className="w-1.5 h-1.5 rounded-full bg-gray-700" />

          {/* Previous arrow */}
          <button
            onClick={handlePrev}
            onTouchEnd={(e) => e.currentTarget.blur()}
            disabled={!hasPrev}
            className={cn(
              "p-1.5 hover:bg-lime-500/10 rounded-lg transition-all duration-200 hover:shadow-[0_0_30px_rgba(255,255,255,0.8)] touch-none",
              !hasPrev && "opacity-30 cursor-not-allowed"
            )}
          >
            <ChevronLeft className="h-4 w-4 text-lime-400" />
          </button>

          {/* Single active dot */}
          <div className="w-2 h-2 rounded-full bg-lime-500 shadow-[0_0_8px_rgba(168,213,48,0.6)]" />

          {/* Counter + Section name */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium">
              {currentIndex + 1}/{sections.length}
            </span>
            <span className="text-xs text-lime-500 font-semibold hidden sm:block">
              {t[sections[currentIndex].key]}
            </span>
          </div>

          {/* Dot separator */}
          <div className="w-1.5 h-1.5 rounded-full bg-gray-700" />

          {/* Next arrow */}
          <button
            onClick={handleNext}
            onTouchEnd={(e) => e.currentTarget.blur()}
            disabled={!hasNext}
            className={cn(
              "p-1.5 hover:bg-lime-500/10 rounded-lg transition-all duration-200 hover:shadow-[0_0_30px_rgba(255,255,255,0.8)] touch-none",
              !hasNext && "opacity-30 cursor-not-allowed"
            )}
          >
            <ChevronRight className="h-4 w-4 text-lime-400" />
          </button>
        </div>

        {/* Buttons - Right side - Order: Présentation, Écosystème, Organigramme, Invest Deck, Ronde Seed, PDF */}
        <div className="hidden lg:flex items-center gap-1 xl:gap-2 flex-shrink-0">
          <button
            onClick={() => window.open('/presentation-ecolunch', '_blank')}
            className="inline-flex items-center gap-1 px-2 xl:px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold bg-gradient-to-r from-lime-400 to-green-500 hover:from-lime-500 hover:to-green-600 text-black transition-all whitespace-nowrap shadow-[0_0_15px_rgba(132,204,22,0.5)] hover:shadow-[0_0_25px_rgba(132,204,22,0.7)]"
            data-testid="presentation-ecolunch-btn"
          >
            <ExternalLink className="h-3 w-3 xl:h-4 xl:w-4" />
            <span className="hidden xl:inline">Présentation</span>
            <span className="xl:hidden">Présent.</span>
          </button>
          <button
            onClick={() => window.open('/ecosysteme-portails', '_blank')}
            className="inline-flex items-center gap-1 px-2 xl:px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all whitespace-nowrap shadow-[0_0_15px_rgba(59,130,246,0.5)] hover:shadow-[0_0_25px_rgba(59,130,246,0.7)]"
            data-testid="ecosysteme-portails-btn"
          >
            <ExternalLink className="h-3 w-3 xl:h-4 xl:w-4" />
            <span className="hidden xl:inline">Écosystème</span>
            <span className="xl:hidden">Écosyst.</span>
          </button>
          <button
            onClick={() => window.open('/organigramme', '_blank')}
            className="inline-flex items-center gap-1 px-2 xl:px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-500 hover:to-teal-600 text-black transition-all whitespace-nowrap shadow-[0_0_15px_rgba(34,211,238,0.5)] hover:shadow-[0_0_25px_rgba(34,211,238,0.7)]"
            data-testid="organigramme-btn"
          >
            <ExternalLink className="h-3 w-3 xl:h-4 xl:w-4" />
            <span className="hidden xl:inline">Organigramme</span>
            <span className="xl:hidden">Organi.</span>
          </button>
          <button
            disabled
            title="Bientôt disponible"
            className="inline-flex items-center gap-1 px-2 xl:px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 whitespace-nowrap opacity-50 cursor-not-allowed"
            data-testid="invest-deck-btn"
          >
            <ExternalLink className="h-3 w-3 xl:h-4 xl:w-4" />
            <span className="hidden xl:inline">Invest Deck</span>
            <span className="xl:hidden">Invest</span>
          </button>
          <button
            disabled
            title="Bientôt disponible"
            className="inline-flex items-center gap-1 px-2 xl:px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold bg-gradient-to-r from-gray-600 to-gray-700 text-gray-400 whitespace-nowrap opacity-50 cursor-not-allowed"
            data-testid="ronde-seed-btn"
          >
            <ExternalLink className="h-3 w-3 xl:h-4 xl:w-4" />
            <span className="hidden xl:inline">Ronde Seed</span>
            <span className="xl:hidden">Seed</span>
          </button>
          <PDFExportButton />
        </div>
      </div>
    </div>
  );
}
